import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Container } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faHeartPulse,
  faBandage,
  faDumbbell,
  faPlus,
  faPen,
  faTrash,
  faCheck,
  faExclamationTriangle,
  faBell,
  faSpinner,
  faDroplet,
  faNotesMedical,
  faCalendarDay,
  faClock,
  faBaby,
  faShieldHeart,
  faCalendarDays,
  faClockRotateLeft,
  faFlagCheckered,
} from '@fortawesome/free-solid-svg-icons';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import womensHealthService, {
  PostpartumProfile,
  PregnancyProfile,
  RecoveryLog,
  PostpartumExercise,
} from '../../../services/womensHealthService';
import BackButton from '../../../components/BackButton/BackButton';
import WHArticleList from '../../../components/WHArticleList/WHArticleList';
import Modal from '../../../components/Modal/Modal';
import ProfileOverviewCard from '../../../components/ProfileOverviewCard/ProfileOverviewCard';
import ConfirmDialog from '../../../components/ConfirmDialog/ConfirmDialog';
import { getApiErrorMessage } from '../../../api/axiosInstance';
import './PostpartumRecovery.css';
import { useProfile } from '../../../context/ProfileContext';

/* ================================================================== */
/*  Types                                                              */
/* ================================================================== */

type TabKey = 'recovery' | 'exercise' | 'pelvic';

interface ToastState {
  message: string;
  type: 'success' | 'error' | 'info';
}

interface ChoiceOption {
  value: string;
  display_name: string;
}

/* ================================================================== */
/*  Helpers                                                            */
/* ================================================================== */

const formatDate = (iso: string | null): string => {
  if (!iso) return '--';
  const d = new Date(iso);
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

const todayStr = (): string => new Date().toISOString().split('T')[0];

const daysSince = (dateStr: string): number => {
  const d = new Date(dateStr);
  const now = new Date();
  return Math.max(0, Math.round((now.getTime() - d.getTime()) / (1000 * 60 * 60 * 24)));
};

const daysBetween = (startIso: string, endIso: string): number => {
  const start = new Date(startIso);
  const end = new Date(endIso);
  return Math.max(0, Math.round((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)));
};

const mapDeliveryType = (pregnancyType?: string): string => {
  switch (pregnancyType) {
    case 'natural':
    case 'vbac':
      return 'vaginal';
    case 'c_section':
      return 'c_section';
    default:
      return '';
  }
};

/* ================================================================== */
/*  Toast                                                              */
/* ================================================================== */

interface ToastProps {
  message: string;
  type: 'success' | 'error' | 'info';
  onClose: () => void;
}

const Toast: React.FC<ToastProps> = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  const iconMap: Record<string, IconDefinition> = {
    success: faCheck,
    error: faExclamationTriangle,
    info: faBell,
  };

  return (
    <div className={`wh-pp-toast wh-pp-toast-${type}`}>
      <span className="wh-pp-toast-icon">
        <FontAwesomeIcon icon={iconMap[type]} />
      </span>
      <span className="wh-pp-toast-message">{message}</span>
      <button className="wh-pp-toast-close" onClick={onClose} aria-label="Close notification">
        {'\u00D7'}
      </button>
    </div>
  );
};

/* ================================================================== */
/*  Skeleton                                                           */
/* ================================================================== */

const SkeletonCards: React.FC = () => (
  <div className="wh-pp-skeleton-grid">
    {[0, 1, 2].map((i) => (
      <div key={i} className="wh-pp-skeleton-card">
        <div className="wh-pp-skeleton-line long" />
        <div className="wh-pp-skeleton-line medium" />
        <div className="wh-pp-skeleton-line short" />
      </div>
    ))}
  </div>
);

/* ================================================================== */
/*  Initial Form States                                                */
/* ================================================================== */

const INITIAL_PROFILE_FORM = {
  delivery_date: '',
  delivery_type_actual: '',
  baby_weight_kg: '' as string | number,
  recovery_mode: '',
};

const INITIAL_RECOVERY_FORM = {
  date: todayStr(),
  bleeding_level: '',
  pain_level: '',
  incision_status: '',
  notes: '',
};

const INITIAL_EXERCISE_FORM = {
  exercise_type: '',
  date: todayStr(),
  duration_minutes: 15,
  completed: false,
  notes: '',
};

/* ================================================================== */
/*  Main Component                                                     */
/* ================================================================== */

const PostpartumRecovery: React.FC = () => {
  const { t } = useTranslation(['womensHealth', 'common']);
  const { activeProfile } = useProfile();
  const [searchParams, setSearchParams] = useSearchParams();

  // Data state
  const [profile, setProfile] = useState<PostpartumProfile | null>(null);
  const [history, setHistory] = useState<PostpartumProfile[]>([]);
  const [recoveryLogs, setRecoveryLogs] = useState<RecoveryLog[]>([]);
  const [exercises, setExercises] = useState<PostpartumExercise[]>([]);
  const [recoveryChoices, setRecoveryChoices] = useState<Record<string, ChoiceOption[]>>({});
  const [exerciseChoices, setExerciseChoices] = useState<Record<string, ChoiceOption[]>>({});
  const [profileChoices, setProfileChoices] = useState<Record<string, ChoiceOption[]>>({});

  // UI state
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<TabKey>((searchParams.get('tab') as TabKey) || 'recovery');
  const [toast, setToast] = useState<ToastState | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<{ id: number; type: 'recovery' | 'exercise' } | null>(null);
  const [showResetProfile, setShowResetProfile] = useState(false);
  const [showCompleteProfile, setShowCompleteProfile] = useState(false);

  // A delivery handed off from the Pregnancy dashboard, used to prefill the
  // recovery setup so it becomes part of the same episode.
  const [linkedPregnancy, setLinkedPregnancy] = useState<PregnancyProfile | null>(null);
  const prefilledRef = useRef(false);

  // The recovery episode just finished, kept so we can show a completion card.
  const [justCompleted, setJustCompleted] = useState<PostpartumProfile | null>(null);

  // Form state
  const [profileForm, setProfileForm] = useState(INITIAL_PROFILE_FORM);
  const [editingProfile, setEditingProfile] = useState(false);
  const [recoveryForm, setRecoveryForm] = useState(INITIAL_RECOVERY_FORM);
  const [exerciseForm, setExerciseForm] = useState(INITIAL_EXERCISE_FORM);
  const [editingRecoveryId, setEditingRecoveryId] = useState<number | null>(null);
  const [editingExerciseId, setEditingExerciseId] = useState<number | null>(null);
  const [showRecoveryForm, setShowRecoveryForm] = useState(false);
  const [showExerciseForm, setShowExerciseForm] = useState(false);

  const showToast = useCallback((message: string, type: ToastState['type']) => {
    setToast({ message, type });
  }, []);

  /* ---------------------------------------------------------------- */
  /*  Data Fetching                                                    */
  /* ---------------------------------------------------------------- */

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [pp, allProfiles, ppChoices] = await Promise.all([
        womensHealthService.getActivePostpartum(),
        womensHealthService.getPostpartumProfiles(),
        womensHealthService.getPostpartumProfileChoices(),
      ]);
      setProfile(pp);
      if (ppChoices) setProfileChoices(ppChoices);
      setHistory(
        (Array.isArray(allProfiles) ? allProfiles : [])
          .filter((p) => p.status !== 'active')
          .sort((a, b) =>
            String(b.completed_date || b.delivery_date).localeCompare(
              String(a.completed_date || a.delivery_date)
            )
          )
      );
      // A newly-started recovery supersedes the "just completed" banner.
      if (pp) setJustCompleted(null);

      if (pp) {
        const [logs, rChoices, exList, eChoices] = await Promise.all([
          womensHealthService.getRecoveryLogs(pp.id),
          womensHealthService.getRecoveryLogChoices(),
          womensHealthService.getPostpartumExercises(pp.id),
          womensHealthService.getPostpartumExerciseChoices(),
        ]);
        setRecoveryLogs(Array.isArray(logs) ? logs : []);
        if (rChoices) setRecoveryChoices(rChoices);
        setExercises(Array.isArray(exList) ? exList : []);
        if (eChoices) setExerciseChoices(eChoices);
      }
    } catch (err) {
      console.error('Failed to fetch postpartum data:', err);
      showToast('Failed to load postpartum data.', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    fetchData();
  }, [fetchData, activeProfile]);

  // Prefill the recovery setup from a delivery handed off by the Pregnancy
  // dashboard (?pregnancy=<id>), and clear the param so a refresh does not
  // re-trigger it.
  useEffect(() => {
    if (loading) return;
    const pregnancyParam = searchParams.get('pregnancy');
    if (!pregnancyParam || prefilledRef.current) return;
    prefilledRef.current = true;

    const next = new URLSearchParams(searchParams);
    next.delete('pregnancy');
    setSearchParams(next, { replace: true });

    const id = Number(pregnancyParam);
    if (!id || profile) return;

    (async () => {
      try {
        const preg = await womensHealthService.getPregnancyProfile(id);
        if (!preg) return;
        setLinkedPregnancy(preg);
        setProfileForm({
          delivery_date: preg.delivery_date || '',
          delivery_type_actual: mapDeliveryType(preg.delivery_type),
          baby_weight_kg: '',
          recovery_mode: 'normal',
        });
      } catch (err) {
        console.error('Failed to load the linked pregnancy:', err);
      }
    })();
  }, [loading, profile, searchParams, setSearchParams]);

  /* ---------------------------------------------------------------- */
  /*  Profile Setup                                                    */
  /* ---------------------------------------------------------------- */

  const handleProfileSubmit = async () => {
    if (!profileForm.delivery_date || !profileForm.delivery_type_actual || !profileForm.recovery_mode) {
      showToast('Please fill in all required fields.', 'error');
      return;
    }
    try {
      setSaving(true);
      const payload: any = {
        delivery_date: profileForm.delivery_date,
        delivery_type_actual: profileForm.delivery_type_actual,
        recovery_mode: profileForm.recovery_mode,
      };
      if (profileForm.baby_weight_kg !== '' && profileForm.baby_weight_kg !== undefined) {
        payload.baby_weight_kg = Number(profileForm.baby_weight_kg);
      }
      if (editingProfile && profile) {
        await womensHealthService.updatePostpartumProfile(profile.id, payload);
        showToast('Postpartum profile updated!', 'success');
      } else {
        if (linkedPregnancy) payload.pregnancy = linkedPregnancy.id;
        await womensHealthService.createPostpartumProfile(payload);
        showToast('Postpartum profile created!', 'success');
        setLinkedPregnancy(null);
        setJustCompleted(null);
      }
      setEditingProfile(false);
      setProfileForm(INITIAL_PROFILE_FORM);
      await fetchData();
    } catch (err) {
      console.error('Error saving profile:', err);
      showToast(getApiErrorMessage(err, 'Failed to save profile.'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const startEditProfile = () => {
    if (!profile) return;
    setProfileForm({
      delivery_date: profile.delivery_date,
      delivery_type_actual: profile.delivery_type_actual,
      baby_weight_kg: profile.baby_weight_kg ?? '',
      recovery_mode: profile.recovery_mode,
    });
    setEditingProfile(true);
  };

  const cancelEditProfile = () => {
    setEditingProfile(false);
    setProfileForm(INITIAL_PROFILE_FORM);
  };

  const handleResetProfile = async () => {
    if (!profile) return;
    try {
      setSaving(true);
      await womensHealthService.deletePostpartumProfile(profile.id);
      setProfile(null);
      setRecoveryLogs([]);
      setExercises([]);
      setEditingProfile(false);
      showToast('Postpartum profile deleted.', 'success');
    } catch (err) {
      console.error('Error deleting postpartum profile:', err);
      showToast('Failed to delete profile.', 'error');
    } finally {
      setSaving(false);
      setShowResetProfile(false);
    }
  };

  const handleCompleteProfile = async () => {
    if (!profile) return;
    try {
      setSaving(true);
      const updated = await womensHealthService.completePostpartum(profile.id);
      setJustCompleted(updated);
      setProfile(null);
      setRecoveryLogs([]);
      setExercises([]);
      setEditingProfile(false);
      showToast('Recovery marked complete.', 'success');
      await fetchData();
    } catch (err) {
      console.error('Error completing recovery:', err);
      showToast('Failed to complete recovery.', 'error');
    } finally {
      setSaving(false);
    }
  };

  /* ---------------------------------------------------------------- */
  /*  Recovery Log CRUD                                                */
  /* ---------------------------------------------------------------- */

  const handleRecoverySave = async () => {
    if (!recoveryForm.date || !recoveryForm.bleeding_level || !recoveryForm.pain_level) {
      showToast('Please fill in date, bleeding level, and pain level.', 'error');
      return;
    }
    try {
      setSaving(true);
      const payload = { ...recoveryForm, postpartum: profile!.id };
      if (editingRecoveryId) {
        await womensHealthService.updateRecoveryLog(editingRecoveryId, payload);
        showToast('Recovery log updated!', 'success');
      } else {
        await womensHealthService.createRecoveryLog(payload);
        showToast('Recovery log added!', 'success');
      }
      setRecoveryForm(INITIAL_RECOVERY_FORM);
      setEditingRecoveryId(null);
      setShowRecoveryForm(false);
      await fetchData();
    } catch (err) {
      console.error('Error saving recovery log:', err);
      showToast(getApiErrorMessage(err, 'Failed to save recovery log.'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleEditRecovery = (log: RecoveryLog) => {
    setRecoveryForm({
      date: log.date,
      bleeding_level: log.bleeding_level,
      pain_level: log.pain_level,
      incision_status: log.incision_status,
      notes: log.notes || '',
    });
    setEditingRecoveryId(log.id);
    setShowRecoveryForm(true);
  };

  /* ---------------------------------------------------------------- */
  /*  Exercise Log CRUD                                                */
  /* ---------------------------------------------------------------- */

  const handleExerciseSave = async () => {
    if (!exerciseForm.exercise_type || !exerciseForm.date) {
      showToast('Please fill in exercise type and date.', 'error');
      return;
    }
    try {
      setSaving(true);
      const payload = { ...exerciseForm, postpartum: profile!.id };
      if (editingExerciseId) {
        await womensHealthService.updatePostpartumExercise(editingExerciseId, payload);
        showToast('Exercise log updated!', 'success');
      } else {
        await womensHealthService.createPostpartumExercise(payload);
        showToast('Exercise log added!', 'success');
      }
      setExerciseForm(INITIAL_EXERCISE_FORM);
      setEditingExerciseId(null);
      setShowExerciseForm(false);
      await fetchData();
    } catch (err) {
      console.error('Error saving exercise:', err);
      showToast(getApiErrorMessage(err, 'Failed to save exercise log.'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleEditExercise = (ex: PostpartumExercise) => {
    setExerciseForm({
      exercise_type: ex.exercise_type,
      date: ex.date,
      duration_minutes: ex.duration_minutes,
      completed: ex.completed,
      notes: ex.notes || '',
    });
    setEditingExerciseId(ex.id);
    setShowExerciseForm(true);
  };

  /* ---------------------------------------------------------------- */
  /*  Delete Handler                                                   */
  /* ---------------------------------------------------------------- */

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      if (confirmDelete.type === 'recovery') {
        await womensHealthService.deleteRecoveryLog(confirmDelete.id);
        showToast('Recovery log deleted.', 'success');
      } else {
        await womensHealthService.deletePostpartumExercise(confirmDelete.id);
        showToast('Exercise log deleted.', 'success');
      }
      setConfirmDelete(null);
      await fetchData();
    } catch (err) {
      console.error('Error deleting:', err);
      showToast('Failed to delete entry.', 'error');
    }
  };

  /* ---------------------------------------------------------------- */
  /*  Tabs Config                                                      */
  /* ---------------------------------------------------------------- */

  const tabs: { key: TabKey; label: string; icon: IconDefinition }[] = [
    { key: 'recovery', label: 'Recovery Log', icon: faBandage },
    { key: 'exercise', label: 'Exercise Log', icon: faDumbbell },
    { key: 'pelvic', label: 'Pelvic Floor Guide', icon: faShieldHeart },
  ];

  /* ================================================================ */
  /*  Render: Profile Setup                                            */
  /* ================================================================ */

  const renderProfileFields = () => (
    <>
      <div className="wh-pp-form-group">
        <label className="wh-pp-form-label">
          Delivery Date <span className="wh-pp-required">*</span>
        </label>
        <input
          type="date"
          className="wh-pp-form-input"
          value={profileForm.delivery_date}
          onChange={(e) => setProfileForm({ ...profileForm, delivery_date: e.target.value })}
        />
      </div>
      <div className="wh-pp-form-row">
        <div className="wh-pp-form-group">
          <label className="wh-pp-form-label">
            Delivery Type <span className="wh-pp-required">*</span>
          </label>
          <select
            className="wh-pp-form-select"
            value={profileForm.delivery_type_actual}
            onChange={(e) => setProfileForm({ ...profileForm, delivery_type_actual: e.target.value })}
          >
            <option value="">Select type...</option>
            {(profileChoices.delivery_type_actual || [
              { value: 'vaginal', display_name: 'Vaginal' },
              { value: 'c_section', display_name: 'C-Section' },
              { value: 'assisted', display_name: 'Assisted' },
            ]).map((c) => (
              <option key={c.value} value={c.value}>{c.display_name}</option>
            ))}
          </select>
        </div>
        <div className="wh-pp-form-group">
          <label className="wh-pp-form-label">Baby Weight (kg)</label>
          <input
            type="number"
            className="wh-pp-form-input"
            step="0.1"
            min="0.5"
            max="7"
            placeholder="e.g. 3.2"
            value={profileForm.baby_weight_kg}
            onChange={(e) => setProfileForm({ ...profileForm, baby_weight_kg: e.target.value })}
          />
        </div>
      </div>
      <div className="wh-pp-form-group">
        <label className="wh-pp-form-label">
          Recovery Mode <span className="wh-pp-required">*</span>
        </label>
        <select
          className="wh-pp-form-select"
          value={profileForm.recovery_mode}
          onChange={(e) => setProfileForm({ ...profileForm, recovery_mode: e.target.value })}
        >
          <option value="">Select mode...</option>
          {(profileChoices.recovery_mode || [
            { value: 'normal', display_name: 'Normal' },
            { value: 'extended', display_name: 'Extended' },
            { value: 'complicated', display_name: 'Complicated' },
          ]).map((c) => (
            <option key={c.value} value={c.value}>{c.display_name}</option>
          ))}
        </select>
      </div>
    </>
  );

  const renderProfileSetup = () => (
    <div className="wh-pp-setup-card">
      <div className="wh-pp-setup-icon-wrap">
        <FontAwesomeIcon icon={faBaby} />
      </div>
      <h2 className="wh-pp-setup-title">
        {linkedPregnancy ? 'Start Your Recovery' : 'Set Up Your Postpartum Profile'}
      </h2>
      <p className="wh-pp-setup-desc">
        Tell us about your delivery so we can personalize your recovery tracking experience.
      </p>
      {linkedPregnancy && (
        <div className="wh-pp-prefill-banner">
          <FontAwesomeIcon icon={faHeartPulse} />
          <span>
            Carried over from your completed pregnancy
            {linkedPregnancy.delivery_date
              ? ` (ended ${formatDate(linkedPregnancy.delivery_date)})`
              : ''}
            . Adjust anything before creating your recovery.
          </span>
        </div>
      )}
      <div className="wh-pp-setup-form">
        {renderProfileFields()}
        <div className="wh-pp-setup-actions">
          <button
            className="wh-pp-btn wh-pp-btn-primary"
            onClick={handleProfileSubmit}
            disabled={saving}
          >
            {saving ? (
              <><FontAwesomeIcon icon={faSpinner} spin /> Creating...</>
            ) : (
              <><FontAwesomeIcon icon={faCheck} /> Create Profile</>
            )}
          </button>
        </div>
      </div>
    </div>
  );

  /* ================================================================ */
  /*  Render: Just-Completed Banner                                    */
  /* ================================================================ */

  const renderCompletedCard = () => {
    if (!justCompleted) return null;
    const duration =
      justCompleted.completed_date
        ? daysBetween(justCompleted.delivery_date, justCompleted.completed_date)
        : null;
    return (
      <div className="wh-pp-complete-card">
        <div className="wh-pp-complete-icon">
          <FontAwesomeIcon icon={faFlagCheckered} />
        </div>
        <div className="wh-pp-complete-body">
          <h2>Recovery complete</h2>
          <div className="wh-pp-history-meta wh-pp-complete-meta">
            <span>
              Delivered {formatDate(justCompleted.delivery_date)}
            </span>
            {justCompleted.completed_date && (
              <span>Completed {formatDate(justCompleted.completed_date)}</span>
            )}
            {duration !== null && (
              <span><strong>{duration}</strong> days of recovery</span>
            )}
            {(justCompleted.delivery_type_actual_display || justCompleted.delivery_type_actual) && (
              <span>
                {justCompleted.delivery_type_actual_display || justCompleted.delivery_type_actual}
              </span>
            )}
          </div>
          <p className="wh-pp-complete-note">
            Your recovery journey is recorded below. Start a new recovery any time you have
            another delivery.
          </p>
        </div>
      </div>
    );
  };

  /* ================================================================ */
  /*  Render: Previous Recoveries (history)                            */
  /* ================================================================ */

  const renderHistory = () => {
    if (history.length === 0) return null;
    return (
      <div className="wh-pp-history">
        <h3 className="wh-pp-history-title">
          <FontAwesomeIcon icon={faClockRotateLeft} /> Previous Recoveries
        </h3>
        <div className="wh-pp-history-list">
          {history.map((p) => {
            const duration = p.completed_date
              ? daysBetween(p.delivery_date, p.completed_date)
              : null;
            return (
              <div key={p.id} className="wh-pp-history-item">
                <span className={`wh-pp-history-badge wh-pp-history-badge-${p.status}`}>
                  {p.status_display || p.status}
                </span>
                <div className="wh-pp-history-dates">
                  <span>Delivered {formatDate(p.delivery_date)}</span>
                  {p.completed_date && <span>Completed {formatDate(p.completed_date)}</span>}
                </div>
                <div className="wh-pp-history-meta">
                  {duration !== null && (
                    <span><strong>{duration}</strong> days of recovery</span>
                  )}
                  {p.delivery_type_actual && (
                    <span>{p.delivery_type_actual_display || p.delivery_type_actual}</span>
                  )}
                  {p.recovery_mode && (
                    <span>{p.recovery_mode_display || p.recovery_mode}</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  /* ================================================================ */
  /*  Render: Profile Summary                                          */
  /* ================================================================ */

  const renderProfileSummary = () => {
    if (!profile) return null;
    return (
      <ProfileOverviewCard
        title="Your Postpartum Profile"
        icon={faHeartPulse}
        accentColor="#a855f7"
        rows={[
          { label: 'Delivery Date', value: formatDate(profile.delivery_date) },
          { label: 'Delivery Type', value: profile.delivery_type_actual_display || profile.delivery_type_actual || '--' },
          { label: 'Days Since Delivery', value: daysSince(profile.delivery_date) },
          { label: 'Recovery Mode', value: profile.recovery_mode_display || profile.recovery_mode || '--' },
        ]}
        onEdit={startEditProfile}
        onReset={() => setShowResetProfile(true)}
        editLabel="Edit"
          resetLabel="Delete profile"
      />
    );
  };

  const renderProfileCompleteAction = () => {
    if (!profile) return null;
    return (
      <div className="wh-pp-complete-action">
        <div className="wh-pp-complete-action-text">
          <strong>Finished recovering?</strong>
          <span>Mark this recovery complete to keep it in your history and start a new one later.</span>
        </div>
        <button
          className="wh-pp-btn wh-pp-btn-complete"
          onClick={() => setShowCompleteProfile(true)}
          disabled={saving}
        >
          <FontAwesomeIcon icon={faFlagCheckered} /> Mark Recovery Complete
        </button>
      </div>
    );
  };

  /* ================================================================ */
  /*  Render: Recovery Log Tab                                         */
  /* ================================================================ */

  const renderRecoveryTab = () => (
    <div className="wh-pp-tab-panel">
      {/* Add / Edit Form */}
      {showRecoveryForm ? (
        <div className="wh-pp-form-card">
          <h3 className="wh-pp-form-card-title">
            <FontAwesomeIcon icon={editingRecoveryId ? faPen : faPlus} />
            {editingRecoveryId ? 'Edit Recovery Log' : 'Add Recovery Log'}
          </h3>
          <div className="wh-pp-form-grid">
            <div className="wh-pp-form-group">
              <label className="wh-pp-form-label">Date <span className="wh-pp-required">*</span></label>
              <input
                type="date"
                className="wh-pp-form-input"
                value={recoveryForm.date}
                onChange={(e) => setRecoveryForm({ ...recoveryForm, date: e.target.value })}
              />
            </div>
            <div className="wh-pp-form-group">
              <label className="wh-pp-form-label">Bleeding Level <span className="wh-pp-required">*</span></label>
              <select
                className="wh-pp-form-select"
                value={recoveryForm.bleeding_level}
                onChange={(e) => setRecoveryForm({ ...recoveryForm, bleeding_level: e.target.value })}
              >
                <option value="">Select...</option>
                {(recoveryChoices.bleeding_level || []).map((c) => (
                  <option key={c.value} value={c.value}>{c.display_name}</option>
                ))}
              </select>
            </div>
            <div className="wh-pp-form-group">
              <label className="wh-pp-form-label">Pain Level <span className="wh-pp-required">*</span></label>
              <select
                className="wh-pp-form-select"
                value={recoveryForm.pain_level}
                onChange={(e) => setRecoveryForm({ ...recoveryForm, pain_level: e.target.value })}
              >
                <option value="">Select...</option>
                {(recoveryChoices.pain_level || []).map((c) => (
                  <option key={c.value} value={c.value}>{c.display_name}</option>
                ))}
              </select>
            </div>
            <div className="wh-pp-form-group">
              <label className="wh-pp-form-label">Incision Status</label>
              <select
                className="wh-pp-form-select"
                value={recoveryForm.incision_status}
                onChange={(e) => setRecoveryForm({ ...recoveryForm, incision_status: e.target.value })}
              >
                <option value="">Select...</option>
                {(recoveryChoices.incision_status || []).map((c) => (
                  <option key={c.value} value={c.value}>{c.display_name}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="wh-pp-form-group" style={{ marginTop: 12 }}>
            <label className="wh-pp-form-label">Notes</label>
            <textarea
              className="wh-pp-form-textarea"
              rows={3}
              placeholder="How are you feeling today..."
              value={recoveryForm.notes}
              onChange={(e) => setRecoveryForm({ ...recoveryForm, notes: e.target.value })}
            />
          </div>
          <div className="wh-pp-form-actions">
            <button
              className="wh-pp-btn wh-pp-btn-cancel"
              onClick={() => {
                setShowRecoveryForm(false);
                setEditingRecoveryId(null);
                setRecoveryForm(INITIAL_RECOVERY_FORM);
              }}
            >
              {t('common:buttons.cancel')}
            </button>
            <button
              className="wh-pp-btn wh-pp-btn-primary"
              onClick={handleRecoverySave}
              disabled={saving}
            >
              {saving ? (
                <><FontAwesomeIcon icon={faSpinner} spin /> Saving...</>
              ) : (
                <><FontAwesomeIcon icon={faCheck} /> {editingRecoveryId ? 'Update' : 'Save'}</>
              )}
            </button>
          </div>
        </div>
      ) : (
        <button
          className="wh-pp-btn wh-pp-btn-primary wh-pp-add-btn"
          onClick={() => {
            setRecoveryForm(INITIAL_RECOVERY_FORM);
            setEditingRecoveryId(null);
            setShowRecoveryForm(true);
          }}
        >
          <FontAwesomeIcon icon={faPlus} /> Add Recovery Log
        </button>
      )}

      {/* List */}
      {loading ? (
        <SkeletonCards />
      ) : recoveryLogs.length === 0 ? (
        <div className="wh-pp-empty-state">
          <div className="wh-pp-empty-icon-wrap">
            <FontAwesomeIcon icon={faBandage} />
          </div>
          <h3>No recovery logs yet</h3>
          <p>Start tracking your postpartum recovery to monitor your healing progress.</p>
        </div>
      ) : (
        <div className="wh-pp-cards-grid">
          {recoveryLogs.map((log) => (
            <div key={log.id} className="wh-pp-entry-card">
              <div className="wh-pp-entry-header">
                <span className="wh-pp-entry-date">
                  <FontAwesomeIcon icon={faCalendarDay} /> {formatDate(log.date)}
                </span>
              </div>
              <div className="wh-pp-badges-row">
                <span className="wh-pp-badge wh-pp-badge-bleeding">
                  <FontAwesomeIcon icon={faDroplet} /> {log.bleeding_level_display || log.bleeding_level}
                </span>
                <span className="wh-pp-badge wh-pp-badge-pain">
                  <FontAwesomeIcon icon={faHeartPulse} /> {log.pain_level_display || log.pain_level}
                </span>
                {log.incision_status && (
                  <span className="wh-pp-badge wh-pp-badge-incision">
                    <FontAwesomeIcon icon={faBandage} /> {log.incision_status_display || log.incision_status}
                  </span>
                )}
              </div>
              {log.notes && (
                <p className="wh-pp-entry-notes">
                  <FontAwesomeIcon icon={faNotesMedical} /> {log.notes}
                </p>
              )}
              <div className="wh-pp-entry-actions">
                <button className="wh-pp-action-btn wh-pp-action-edit" onClick={() => handleEditRecovery(log)}>
                  <FontAwesomeIcon icon={faPen} /> {t('common:buttons.edit')}
                </button>
                <button className="wh-pp-action-btn wh-pp-action-delete" onClick={() => setConfirmDelete({ id: log.id, type: 'recovery' })}>
                  <FontAwesomeIcon icon={faTrash} /> {t('common:buttons.delete')}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  /* ================================================================ */
  /*  Render: Exercise Log Tab                                         */
  /* ================================================================ */

  const renderExerciseTab = () => (
    <div className="wh-pp-tab-panel">
      {showExerciseForm ? (
        <div className="wh-pp-form-card">
          <h3 className="wh-pp-form-card-title">
            <FontAwesomeIcon icon={editingExerciseId ? faPen : faPlus} />
            {editingExerciseId ? 'Edit Exercise Log' : 'Add Exercise Log'}
          </h3>
          <div className="wh-pp-form-grid">
            <div className="wh-pp-form-group">
              <label className="wh-pp-form-label">Exercise Type <span className="wh-pp-required">*</span></label>
              <select
                className="wh-pp-form-select"
                value={exerciseForm.exercise_type}
                onChange={(e) => setExerciseForm({ ...exerciseForm, exercise_type: e.target.value })}
              >
                <option value="">Select...</option>
                {(exerciseChoices.exercise_type || []).map((c) => (
                  <option key={c.value} value={c.value}>{c.display_name}</option>
                ))}
              </select>
            </div>
            <div className="wh-pp-form-group">
              <label className="wh-pp-form-label">Date <span className="wh-pp-required">*</span></label>
              <input
                type="date"
                className="wh-pp-form-input"
                value={exerciseForm.date}
                onChange={(e) => setExerciseForm({ ...exerciseForm, date: e.target.value })}
              />
            </div>
            <div className="wh-pp-form-group">
              <label className="wh-pp-form-label">Duration (minutes)</label>
              <input
                type="number"
                className="wh-pp-form-input"
                min={1}
                max={180}
                value={exerciseForm.duration_minutes}
                onChange={(e) => setExerciseForm({ ...exerciseForm, duration_minutes: parseInt(e.target.value) || 15 })}
              />
            </div>
            <div className="wh-pp-form-group wh-pp-checkbox-group">
              <label className="wh-pp-checkbox-label">
                <input
                  type="checkbox"
                  checked={exerciseForm.completed}
                  onChange={(e) => setExerciseForm({ ...exerciseForm, completed: e.target.checked })}
                />
                <span className="wh-pp-checkbox-custom">
                  {exerciseForm.completed && <FontAwesomeIcon icon={faCheck} />}
                </span>
                Completed
              </label>
            </div>
          </div>
          <div className="wh-pp-form-group" style={{ marginTop: 12 }}>
            <label className="wh-pp-form-label">Notes</label>
            <textarea
              className="wh-pp-form-textarea"
              rows={3}
              placeholder="How did the exercise go..."
              value={exerciseForm.notes}
              onChange={(e) => setExerciseForm({ ...exerciseForm, notes: e.target.value })}
            />
          </div>
          <div className="wh-pp-form-actions">
            <button
              className="wh-pp-btn wh-pp-btn-cancel"
              onClick={() => {
                setShowExerciseForm(false);
                setEditingExerciseId(null);
                setExerciseForm(INITIAL_EXERCISE_FORM);
              }}
            >
              {t('common:buttons.cancel')}
            </button>
            <button
              className="wh-pp-btn wh-pp-btn-primary"
              onClick={handleExerciseSave}
              disabled={saving}
            >
              {saving ? (
                <><FontAwesomeIcon icon={faSpinner} spin /> Saving...</>
              ) : (
                <><FontAwesomeIcon icon={faCheck} /> {editingExerciseId ? 'Update' : 'Save'}</>
              )}
            </button>
          </div>
        </div>
      ) : (
        <button
          className="wh-pp-btn wh-pp-btn-primary wh-pp-add-btn"
          onClick={() => {
            setExerciseForm(INITIAL_EXERCISE_FORM);
            setEditingExerciseId(null);
            setShowExerciseForm(true);
          }}
        >
          <FontAwesomeIcon icon={faPlus} /> Add Exercise Log
        </button>
      )}

      {loading ? (
        <SkeletonCards />
      ) : exercises.length === 0 ? (
        <div className="wh-pp-empty-state">
          <div className="wh-pp-empty-icon-wrap">
            <FontAwesomeIcon icon={faDumbbell} />
          </div>
          <h3>No exercise logs yet</h3>
          <p>Start logging your postpartum exercises to track your fitness recovery.</p>
        </div>
      ) : (
        <div className="wh-pp-cards-grid">
          {exercises.map((ex) => (
            <div key={ex.id} className="wh-pp-entry-card">
              <div className="wh-pp-entry-header">
                <span className="wh-pp-entry-date">
                  <FontAwesomeIcon icon={faCalendarDay} /> {formatDate(ex.date)}
                </span>
                <span className={`wh-pp-badge ${ex.completed ? 'wh-pp-badge-completed' : 'wh-pp-badge-pending'}`}>
                  <FontAwesomeIcon icon={ex.completed ? faCheck : faClock} />
                  {ex.completed ? 'Completed' : 'Pending'}
                </span>
              </div>
              <div className="wh-pp-badges-row">
                <span className="wh-pp-badge wh-pp-badge-type">
                  <FontAwesomeIcon icon={faDumbbell} /> {ex.exercise_type_display || ex.exercise_type}
                </span>
                <span className="wh-pp-badge wh-pp-badge-duration">
                  <FontAwesomeIcon icon={faClock} /> {ex.duration_minutes} min
                </span>
              </div>
              {ex.notes && (
                <p className="wh-pp-entry-notes">
                  <FontAwesomeIcon icon={faNotesMedical} /> {ex.notes}
                </p>
              )}
              <div className="wh-pp-entry-actions">
                <button className="wh-pp-action-btn wh-pp-action-edit" onClick={() => handleEditExercise(ex)}>
                  <FontAwesomeIcon icon={faPen} /> {t('common:buttons.edit')}
                </button>
                <button className="wh-pp-action-btn wh-pp-action-delete" onClick={() => setConfirmDelete({ id: ex.id, type: 'exercise' })}>
                  <FontAwesomeIcon icon={faTrash} /> {t('common:buttons.delete')}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  /* ================================================================ */
  /*  Render: Pelvic Floor Guide Tab                                   */
  /* ================================================================ */

  const renderPelvicTab = () => (
    <div className="wh-pp-tab-panel">
      <div className="wh-pp-guide-intro">
        <h3>Pelvic Floor Rehabilitation Guide</h3>
        <p>
          Restoring pelvic floor strength is essential after delivery. These exercises help improve
          bladder control, support your pelvic organs, and speed your overall recovery. Always
          consult your healthcare provider before beginning any exercise program postpartum.
        </p>
      </div>

      <WHArticleList
        stage="postpartum"
        contentType="pelvic_floor"
        variant="accordion"
        icon={faShieldHeart}
        accentColor="#ec4899"
      />

      <div className="wh-pp-schedule-section">
        <h3 className="wh-pp-schedule-title">Progressive Schedule</h3>
        <WHArticleList
          stage="postpartum"
          contentType="recovery_schedule"
          variant="card"
          icon={faCalendarDays}
          accentColor="#8b5cf6"
        />
      </div>
    </div>
  );

  /* ================================================================ */
  /*  Main Render                                                      */
  /* ================================================================ */

  return (
    <div className="wh-pp-page">
      {/* Toast */}
      {toast && (
        <div className="wh-pp-toast-container">
          <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
        </div>
      )}

      {/* Delete Entry Confirmation */}
      <ConfirmDialog
        open={confirmDelete !== null}
        title="Delete Entry?"
        message="This action cannot be undone. Are you sure?"
        confirmLabel={t('common:buttons.delete')}
        cancelLabel={t('common:buttons.cancel')}
        variant="danger"
        onConfirm={handleDelete}
        onClose={() => setConfirmDelete(null)}
      />

      {/* Delete Profile Confirmation */}
      <ConfirmDialog
        open={showResetProfile}
        title="Delete postpartum profile?"
        message="This permanently removes this postpartum profile and all related recovery data (recovery logs, mood logs, feeding logs, exercises). This cannot be undone."
        confirmLabel="Delete"
        cancelLabel={t('common:buttons.cancel')}
        variant="danger"
        onConfirm={handleResetProfile}
        onClose={() => setShowResetProfile(false)}
      />

      {/* Complete Recovery Confirmation */}
      <ConfirmDialog
        open={showCompleteProfile}
        title="Mark recovery complete?"
        message="This closes your current recovery and moves it to your history. You can start a new recovery after another delivery. Your logs are kept."
        confirmLabel="Mark Complete"
        cancelLabel={t('common:buttons.cancel')}
        variant="primary"
        onConfirm={handleCompleteProfile}
        onClose={() => setShowCompleteProfile(false)}
      />

      {/* Edit Profile Modal */}
      <Modal
        open={editingProfile}
        onClose={cancelEditProfile}
        title="Edit Your Postpartum Profile"
        icon={faBaby}
        size="md"
        footer={
          <>
            <button
              className="wh-pp-btn wh-pp-btn-cancel"
              onClick={cancelEditProfile}
              disabled={saving}
            >
              {t('common:buttons.cancel')}
            </button>
            <button
              className="wh-pp-btn wh-pp-btn-primary"
              onClick={handleProfileSubmit}
              disabled={saving}
            >
              {saving ? (
                <><FontAwesomeIcon icon={faSpinner} spin /> Saving...</>
              ) : (
                <><FontAwesomeIcon icon={faCheck} /> Save Changes</>
              )}
            </button>
          </>
        }
      >
        <div className="wh-pp-setup-form">{renderProfileFields()}</div>
      </Modal>

      {/* Hero Banner */}
      <section className="wh-pp-hero">
        <div className="wh-pp-hero-circle wh-pp-hero-circle-1" />
        <div className="wh-pp-hero-circle wh-pp-hero-circle-2" />
        <Container>
          <BackButton />
          <div className="wh-pp-hero-content">
            <div className="wh-pp-hero-badge">
              <FontAwesomeIcon icon={faHeartPulse} />
              Postpartum
            </div>
            <h1 className="wh-pp-hero-title">{t('womensHealth:postpartum.recovery.title')}</h1>
            <p className="wh-pp-hero-subtitle">
              Track your recovery progress, log exercises, and follow guided pelvic floor rehabilitation after delivery.
            </p>
          </div>
        </Container>
      </section>

      {/* Summary / Setup */}
      <section className="wh-pp-summary-section">
        <Container>
          {loading ? (
            <SkeletonCards />
          ) : profile ? (
            <>
              {renderProfileSummary()}
              {renderProfileCompleteAction()}
            </>
          ) : (
            <>
              {justCompleted && renderCompletedCard()}
              {renderProfileSetup()}
              {renderHistory()}
            </>
          )}
        </Container>
      </section>

      {/* Main Content */}
      {profile && (
        <Container>
          <div className="wh-pp-tabs-container">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                className={`wh-pp-tab-btn ${activeTab === tab.key ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.key)}
              >
                <FontAwesomeIcon icon={tab.icon} />
                {tab.label}
              </button>
            ))}
          </div>

          <div className="wh-pp-tab-content">
            {activeTab === 'recovery' && renderRecoveryTab()}
            {activeTab === 'exercise' && renderExerciseTab()}
            {activeTab === 'pelvic' && renderPelvicTab()}
          </div>
        </Container>
      )}
    </div>
  );
};

export default PostpartumRecovery;
